/**
 * Zod schemas for block properties validation.
 *
 * This module provides runtime validation for block-specific properties.
 * Use these schemas to validate user input before creating or updating blocks.
 *
 * Naming Convention: Properties use camelCase to match TypeScript conventions.
 * Database columns use snake_case (converted during persistence).
 */

import { z } from 'zod'
import type { VocabularyCategory } from '@/db/Types'

// ========== Play Call Types ==========

/**
 * A single chip in a play call sequence.
 * Represents one parsed token from a play call string (e.g., "3 I Z 44 Power").
 */
export interface PlayCallChip {
	text: string
	vocabulary_id: string | null
	category: VocabularyCategory | null
	order: number
}

const VocabularyCategorySchema = z.enum(['formation', 'modifier', 'motion', 'route', 'blocking', 'protection'])

/** Zod schema for a single play call chip. */
export const PlayCallChipSchema = z.object({
	text: z.string().min(1).max(100),
	vocabulary_id: z.string().uuid().nullable(),
	category: VocabularyCategorySchema.nullable(),
	order: z.number().int().min(0),
})

// ========== Shared Enums ==========

export const HashPositionSchema = z.enum(['left', 'middle', 'right'])
export type HashPosition = z.infer<typeof HashPositionSchema>

export const TargetingModeSchema = z.enum(['absolute_role', 'relative_selector'])
export type TargetingMode = z.infer<typeof TargetingModeSchema>

export const BallPositionSchema = z.enum(['left', 'center', 'right'])
export type BallPosition = z.infer<typeof BallPositionSchema>

export const PlayDirectionSchema = z.enum(['left', 'right', 'na'])
export type PlayDirection = z.infer<typeof PlayDirectionSchema>

export const DocumentTypeSchema = z.enum(['call_sheet', 'wristband', 'practice_script'])
export type DocumentType = z.infer<typeof DocumentTypeSchema>

export const DocumentCanvasPresetSchema = z.enum(['letter', 'legal', 'tabloid', 'a4', 'custom'])
export type DocumentCanvasPreset = z.infer<typeof DocumentCanvasPresetSchema>

export const DocumentTextDensitySchema = z.enum(['compact', 'normal', 'spacious'])
export type DocumentTextDensity = z.infer<typeof DocumentTextDensitySchema>

export const ControlPointTypeSchema = z.enum(['start', 'end', 'corner', 'intermediate', 'control', 'snap'])
export type ControlPointType = z.infer<typeof ControlPointTypeSchema>

export const BezierHandleTypeSchema = z.enum(['in', 'out'])
export type BezierHandleType = z.infer<typeof BezierHandleTypeSchema>

export const PathSegmentTypeSchema = z.enum(['line', 'quadratic', 'cubic'])
export type PathSegmentType = z.infer<typeof PathSegmentTypeSchema>

export const LineStyleSchema = z.enum(['solid', 'dashed'])
export type LineStyle = z.infer<typeof LineStyleSchema>

export const LineEndSchema = z.enum(['none', 'arrow', 'tShape'])
export type LineEnd = z.infer<typeof LineEndSchema>

export const PathModeSchema = z.enum(['sharp', 'curve'])
export type PathMode = z.infer<typeof PathModeSchema>

export const LeverageShadingSchema = z.enum(['left', 'right', 'none'])
export type LeverageShading = z.infer<typeof LeverageShadingSchema>

export const PreSnapMotionTypeSchema = z.enum(['motion', 'shift'])
export type PreSnapMotionType = z.infer<typeof PreSnapMotionTypeSchema>

export const TextAlignmentSchema = z.enum(['left', 'center', 'right'])
export type TextAlignment = z.infer<typeof TextAlignmentSchema>

export const DividerStyleSchema = z.enum(['solid', 'dashed', 'dotted'])
export type DividerStyle = z.infer<typeof DividerStyleSchema>

export const ShapeTypeSchema = z.enum(['rectangle', 'ellipse', 'line', 'arrow', 'triangle', 'polygon'])
export type ShapeType = z.infer<typeof ShapeTypeSchema>

export const LayoutModeSchema = z.enum(['free', 'row', 'column', 'grid'])
export type LayoutModeEnum = z.infer<typeof LayoutModeSchema>

export const LayoutAlignSchema = z.enum(['start', 'center', 'end', 'stretch', 'space-between'])
export type LayoutAlignEnum = z.infer<typeof LayoutAlignSchema>

export const CoordinateSystemSchema = z.enum(['field', 'canvas', 'percentage'])
export type CoordinateSystem = z.infer<typeof CoordinateSystemSchema>

// ========== Shared Validators ==========

/** Hex color validation (6-digit with #) */
const HexColorSchema = z
	.string()
	.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid 6-digit hex color (e.g., #FF0000)')

/** Optional hex color */
const OptionalHexColorSchema = HexColorSchema.optional()

/** Header level 1-6 */
const HeaderLevelSchema = z.union([
	z.literal(1),
	z.literal(2),
	z.literal(3),
	z.literal(4),
	z.literal(5),
	z.literal(6),
])

// ========== Layout Properties Schema ==========

/**
 * Layout properties for container blocks.
 * Controls how children are arranged within the container.
 * All fields are optional for backward compatibility.
 */
export const LayoutPropertiesSchema = z
	.object({
		/** How children are arranged (NULL/undefined = inherit or use free positioning) */
		mode: LayoutModeSchema.optional(),
		/** Gap between children in layout flow (pixels/points) */
		gap: z.number().min(0).optional(),
		/** Padding inside the container */
		padding: z.number().min(0).optional(),
		/** Main axis alignment (row: horizontal, column: vertical) */
		alignMain: LayoutAlignSchema.optional(),
		/** Cross axis alignment */
		alignCross: LayoutAlignSchema.optional(),
		/** Whether children wrap to new lines when container overflows */
		wrap: z.boolean().optional(),
		/** Grid columns (for grid layout mode) */
		gridColumns: z.number().int().min(1).optional(),
		/** Grid rows (for grid layout mode) */
		gridRows: z.number().int().min(1).optional(),
	})
	.optional()

export type LayoutProperties = z.infer<typeof LayoutPropertiesSchema>

// ========== Root Block Schemas ==========

/**
 * Player display settings schema.
 * Controls visual appearance of players in the play editor.
 */
export const PlayDisplaySettingsSchema = z.object({
	showLinemanLabels: z.boolean().optional(),
	showOffenseOutline: z.boolean().optional(),
	showDefenseOutline: z.boolean().optional(),
})
export type PlayDisplaySettings = z.infer<typeof PlayDisplaySettingsSchema>

/**
 * Play block properties.
 * A complete play diagram with formations, players, and routes.
 */
export const PlayPropertiesSchema = z.object({
	name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
	hashPosition: HashPositionSchema.default('middle'),
	notes: z.string().max(10000, 'Notes too long').optional(),
	displaySettings: PlayDisplaySettingsSchema.optional(),
	callChips: z.array(PlayCallChipSchema).optional(),
})
export type PlayProperties = z.infer<typeof PlayPropertiesSchema>

/**
 * Formation block properties.
 * Template for player positions.
 */
export const FormationPropertiesSchema = z.object({
	name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
	description: z.string().max(2000, 'Description too long').optional(),
	hashMode: z.boolean().default(false),
	thumbnail: z.string().optional(),
})
export type FormationProperties = z.infer<typeof FormationPropertiesSchema>

/**
 * Concept block properties.
 * Formation with route templates for targeting.
 */
export const ConceptPropertiesSchema = z.object({
	name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
	description: z.string().max(2000, 'Description too long').optional(),
	targetingMode: TargetingModeSchema.default('absolute_role'),
	ballPosition: BallPositionSchema.default('center'),
	playDirection: PlayDirectionSchema.default('na'),
})
export type ConceptProperties = z.infer<typeof ConceptPropertiesSchema>

/**
 * Document block properties.
 * Printable documents like call sheets, wristbands, practice scripts.
 */
export const DocumentPropertiesSchema = z.object({
	name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
	description: z.string().max(2000, 'Description too long').optional(),
	documentType: DocumentTypeSchema.default('call_sheet'),
	canvasPreset: DocumentCanvasPresetSchema.optional(),
	customWidth: z.number().min(4).max(24).optional(),
	customHeight: z.number().min(4).max(36).optional(),
	textDensity: DocumentTextDensitySchema.optional(),
	backgroundColor: OptionalHexColorSchema,
	layout: LayoutPropertiesSchema,
})
export type DocumentProperties = z.infer<typeof DocumentPropertiesSchema>

// ========== Canvas Element Schemas ==========

/**
 * Player block properties.
 * Individual player on the canvas.
 */
export const PlayerPropertiesSchema = z.object({
	label: z.string().max(10, 'Label too long'),
	color: HexColorSchema,
	isLineman: z.boolean().default(false),
	isGhost: z.boolean().default(false),
	role: z.string().max(100, 'Role too long').optional(),
	hashRelative: z.boolean().default(false),
	hashMark: z.enum(['left', 'middle', 'right']).nullable().optional(),
	leverageShading: LeverageShadingSchema.default('none'),
	// Spatial positioning
	x: z.number().optional(),
	y: z.number().optional(),
	coordinateSystem: CoordinateSystemSchema.optional(),
})
export type PlayerProperties = z.infer<typeof PlayerPropertiesSchema>

/**
 * Drawing style sub-schema.
 * Visual properties for routes and paths.
 */
export const DrawingStyleSchema = z.object({
	color: HexColorSchema.default('#000000'),
	// strokeWidth is in field coordinates (feet). A 2px line at 20px/ft scale = 0.1 feet
	strokeWidth: z.number().min(0.01).max(10).default(2),
	// brushSize stores the original pixel value from BRUSH_SIZES (3, 5, 7, 10)
	brushSize: z.number().min(1).max(20).optional(),
	lineStyle: LineStyleSchema.default('solid'),
	lineEnd: LineEndSchema.default('arrow'),
	pathMode: PathModeSchema.default('curve'),
})
export type DrawingStyle = z.infer<typeof DrawingStyleSchema>

/**
 * Drawing block properties.
 * Route or path container with style information.
 */
export const DrawingPropertiesSchema = z.object({
	style: DrawingStyleSchema.optional(),
	templateId: z.string().max(100, 'Template ID too long').optional(),
	selectorType: z.string().max(50, 'Selector type too long').optional(),
	selectorParams: z.record(z.string(), z.unknown()).optional(),
})
export type DrawingProperties = z.infer<typeof DrawingPropertiesSchema>

/**
 * Bezier handle offset - relative to parent control point.
 */
export const BezierHandleOffsetSchema = z.object({
	x: z.number().default(0),
	y: z.number().default(0),
})
export type BezierHandleOffset = z.infer<typeof BezierHandleOffsetSchema>

/**
 * Embedded bezier handles on a control point.
 * Replaces the separate BezierHandleBlock children.
 */
export const EmbeddedHandlesSchema = z.object({
	in: BezierHandleOffsetSchema.optional(),
	out: BezierHandleOffsetSchema.optional(),
})
export type EmbeddedHandles = z.infer<typeof EmbeddedHandlesSchema>

/**
 * Control point block properties.
 * Bezier control point within a drawing.
 * Now includes embedded handles (previously separate BezierHandleBlock children).
 */
export const ControlPointPropertiesSchema = z.object({
	pointType: ControlPointTypeSchema.default('intermediate'),
	isSnapped: z.boolean().default(false),
	handles: EmbeddedHandlesSchema.optional(),
	// Spatial positioning
	x: z.number().optional(),
	y: z.number().optional(),
	coordinateSystem: CoordinateSystemSchema.optional(),
})
export type ControlPointProperties = z.infer<typeof ControlPointPropertiesSchema>

/**
 * @deprecated Use EmbeddedHandlesSchema on ControlPointProperties instead.
 * BezierHandleBlock is being eliminated - handles are now embedded in control points.
 *
 * Bezier handle block properties.
 * Curve handle attached to a control point.
 */
export const BezierHandlePropertiesSchema = z.object({
	handleType: BezierHandleTypeSchema,
	offsetX: z.number().default(0),
	offsetY: z.number().default(0),
})
/** @deprecated Use EmbeddedHandles on ControlPointProperties instead. */
export type BezierHandleProperties = z.infer<typeof BezierHandlePropertiesSchema>

/**
 * Path segment block properties.
 * Segment connecting two control points.
 */
export const PathSegmentPropertiesSchema = z.object({
	segmentType: PathSegmentTypeSchema.default('line'),
})
export type PathSegmentProperties = z.infer<typeof PathSegmentPropertiesSchema>

/**
 * Annotation block properties.
 * Text label on the canvas.
 */
export const AnnotationPropertiesSchema = z.object({
	text: z.string().min(1, 'Text is required').max(500, 'Text too long'),
	color: OptionalHexColorSchema,
	fontSize: z.number().min(6).max(72).optional(),
	offsetX: z.number().default(0),
	offsetY: z.number().default(0),
	// Spatial positioning
	x: z.number().optional(),
	y: z.number().optional(),
	coordinateSystem: CoordinateSystemSchema.optional(),
})
export type AnnotationProperties = z.infer<typeof AnnotationPropertiesSchema>

/**
 * Pre-snap marker block properties.
 * Motion or shift indicator on a drawing.
 */
export const PreSnapMarkerPropertiesSchema = z.object({
	motionType: PreSnapMotionTypeSchema.default('motion'),
	/** FK to ControlPointBlock marking the snap point. Stored in properties (not a column). */
	snapPointId: z.number().int().positive().optional(),
})
export type PreSnapMarkerProperties = z.infer<typeof PreSnapMarkerPropertiesSchema>

// ========== Content Block Schemas ==========

/**
 * Section block properties.
 * Grouping within call sheets or documents.
 */
export const SectionPropertiesSchema = z.object({
	name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
	color: OptionalHexColorSchema,
	collapsed: z.boolean().default(false),
	layout: LayoutPropertiesSchema,
})
export type SectionProperties = z.infer<typeof SectionPropertiesSchema>

/**
 * Text block properties.
 * Text content in documents and presentations.
 */
export const TextPropertiesSchema = z.object({
	content: z.string().min(1, 'Content is required').max(50000, 'Content too long'),
	alignment: TextAlignmentSchema.default('left'),
	fontSize: z.number().min(6).max(144).optional(),
	color: OptionalHexColorSchema,
	// Spatial positioning (for text blocks on slides/canvas)
	x: z.number().optional(),
	y: z.number().optional(),
	width: z.number().min(0).optional(),
	height: z.number().min(0).optional(),
	coordinateSystem: CoordinateSystemSchema.optional(),
})
export type TextProperties = z.infer<typeof TextPropertiesSchema>

/**
 * Header block properties.
 * Section header in documents.
 */
export const HeaderPropertiesSchema = z.object({
	level: HeaderLevelSchema.default(1),
	text: z.string().min(1, 'Text is required').max(500, 'Text too long'),
})
export type HeaderProperties = z.infer<typeof HeaderPropertiesSchema>

/**
 * Divider block properties.
 * Visual separator in documents.
 */
export const DividerPropertiesSchema = z.object({
	style: DividerStyleSchema.default('solid'),
	thickness: z.number().min(1).max(10).default(1),
	color: OptionalHexColorSchema,
})
export type DividerProperties = z.infer<typeof DividerPropertiesSchema>

/**
 * Shape block properties.
 * Geometric shape on slides or documents.
 */
export const ShapePropertiesSchema = z.object({
	shapeType: ShapeTypeSchema,
	strokeColor: OptionalHexColorSchema,
	fillColor: OptionalHexColorSchema,
	strokeWidth: z.number().min(0).max(20).optional(),
	// Spatial positioning
	x: z.number().optional(),
	y: z.number().optional(),
	width: z.number().min(0).optional(),
	height: z.number().min(0).optional(),
	coordinateSystem: CoordinateSystemSchema.optional(),
})
export type ShapeProperties = z.infer<typeof ShapePropertiesSchema>

/**
 * Image block properties.
 * Image reference in slides or documents.
 */
export const ImagePropertiesSchema = z.object({
	url: z.string().url('Must be a valid URL'),
	alt: z.string().optional(),
	// Spatial positioning
	x: z.number().optional(),
	y: z.number().optional(),
	width: z.number().min(0).optional(),
	height: z.number().min(0).optional(),
	coordinateSystem: CoordinateSystemSchema.optional(),
})
export type ImageProperties = z.infer<typeof ImagePropertiesSchema>

// ========== Layout Container Schemas ==========

/**
 * Row block properties.
 * Horizontal layout container.
 */
export const RowPropertiesSchema = z.object({
	layout: LayoutPropertiesSchema,
})
export type RowProperties = z.infer<typeof RowPropertiesSchema>

/**
 * Column block properties.
 * Vertical layout container.
 */
export const ColumnPropertiesSchema = z.object({
	layout: LayoutPropertiesSchema,
})
export type ColumnProperties = z.infer<typeof ColumnPropertiesSchema>

// ========== Block Type Enum ==========

export const BlockTypeSchema = z.enum([
	// Root blocks
	'play',
	'formation',
	'concept',
	'document',
	// Canvas elements
	'player',
	'drawing',
	'control_point',
	// 'bezier_handle' - REMOVED: handles now embedded in control_point properties
	'path_segment',
	'annotation',
	'pre_snap_marker',
	// Content blocks
	'section',
	'text',
	'header',
	'divider',
	'shape',
	'image',
	// Layout containers
	'row',
	'column',
])
export type BlockType = z.infer<typeof BlockTypeSchema>

// ========== Schema Registry ==========

/**
 * Registry mapping type to its properties schema.
 * Use this for dynamic validation based on block type.
 */
export const BlockPropertiesSchemaRegistry = {
	// Root blocks
	play: PlayPropertiesSchema,
	formation: FormationPropertiesSchema,
	concept: ConceptPropertiesSchema,
	document: DocumentPropertiesSchema,
	// Canvas elements
	player: PlayerPropertiesSchema,
	drawing: DrawingPropertiesSchema,
	control_point: ControlPointPropertiesSchema,
	// bezier_handle - REMOVED: handles now embedded in control_point properties
	path_segment: PathSegmentPropertiesSchema,
	annotation: AnnotationPropertiesSchema,
	pre_snap_marker: PreSnapMarkerPropertiesSchema,
	// Content blocks
	section: SectionPropertiesSchema,
	text: TextPropertiesSchema,
	header: HeaderPropertiesSchema,
	divider: DividerPropertiesSchema,
	shape: ShapePropertiesSchema,
	image: ImagePropertiesSchema,
	// Layout containers
	row: RowPropertiesSchema,
	column: ColumnPropertiesSchema,
} as const

/**
 * Type for the schema registry.
 */
export type BlockPropertiesSchemaRegistryType = typeof BlockPropertiesSchemaRegistry

/**
 * Union type of all block properties types.
 */
export type BlockProperties =
	| PlayProperties
	| FormationProperties
	| ConceptProperties
	| DocumentProperties
	| PlayerProperties
	| DrawingProperties
	| ControlPointProperties
	// BezierHandleProperties - REMOVED: handles now embedded in ControlPointProperties
	| PathSegmentProperties
	| AnnotationProperties
	| PreSnapMarkerProperties
	| SectionProperties
	| TextProperties
	| HeaderProperties
	| DividerProperties
	| ShapeProperties
	| ImageProperties
	| RowProperties
	| ColumnProperties

/**
 * Type helper to get properties type for a specific block type.
 */
export type BlockPropertiesFor<T extends BlockType> = z.infer<BlockPropertiesSchemaRegistryType[T]>

// ========== Validation Function ==========

/**
 * Result type for validation function.
 */
export type ValidationResult<T> =
	| { success: true; data: T }
	| { success: false; error: z.ZodError }

/**
 * Validates block properties against the appropriate schema.
 *
 * @param blockType - The type of block to validate against
 * @param properties - The properties object to validate
 * @returns Validated and typed properties, or throws ZodError
 *
 * @example
 * ```ts
 * const result = validateBlockProperties('play', { name: 'Jet Sweep', hashPosition: 'left' })
 * if (result.success) {
 *   console.log(result.data.name) // Typed as PlayProperties
 * }
 * ```
 */
export function validateBlockProperties<T extends BlockType>(
	blockType: T,
	properties: unknown
): ValidationResult<BlockPropertiesFor<T>> {
	const schema = BlockPropertiesSchemaRegistry[blockType]
	if (!schema) {
		return {
			success: false,
			error: new z.ZodError([
				{
					code: 'custom',
					message: `Unknown block type: ${blockType}`,
					path: ['blockType'],
				},
			]),
		}
	}

	const result = schema.safeParse(properties)
	if (result.success) {
		return { success: true, data: result.data as BlockPropertiesFor<T> }
	}
	return { success: false, error: result.error }
}

/**
 * Validates block properties and throws on error.
 * Use when you want exceptions rather than result objects.
 *
 * @param blockType - The type of block to validate against
 * @param properties - The properties object to validate
 * @returns Validated and typed properties
 * @throws ZodError if validation fails
 */
export function parseBlockProperties<T extends BlockType>(
	blockType: T,
	properties: unknown
): BlockPropertiesFor<T> {
	const schema = BlockPropertiesSchemaRegistry[blockType]
	if (!schema) {
		throw new z.ZodError([
			{
				code: 'custom',
				message: `Unknown block type: ${blockType}`,
				path: ['blockType'],
			},
		])
	}

	return schema.parse(properties) as BlockPropertiesFor<T>
}

/**
 * Creates a partial schema for the given block type.
 * Useful for update operations where all fields are optional.
 *
 * @param blockType - The type of block to get partial schema for
 * @returns A Zod schema with all fields optional
 */
export function getPartialSchema<T extends BlockType>(
	blockType: T
): z.ZodType<Partial<BlockPropertiesFor<T>>> {
	const schema = BlockPropertiesSchemaRegistry[blockType]
	if (!schema) {
		throw new Error(`Unknown block type: ${blockType}`)
	}
	// Cast through unknown to handle Zod's complex partial type inference
	return schema.partial() as unknown as z.ZodType<Partial<BlockPropertiesFor<T>>>
}

/**
 * Validates partial block properties for update operations.
 *
 * @param blockType - The type of block to validate against
 * @param properties - The partial properties object to validate
 * @returns Validated partial properties
 */
export function validatePartialBlockProperties<T extends BlockType>(
	blockType: T,
	properties: unknown
): ValidationResult<Partial<BlockPropertiesFor<T>>> {
	const schema = getPartialSchema(blockType)
	const result = schema.safeParse(properties)
	if (result.success) {
		return { success: true, data: result.data }
	}
	return { success: false, error: result.error }
}
